import { Flex } from "@chakra-ui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import { isEmpty } from "lodash/fp";
import moment, { Moment } from "moment";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";

import { CURRENT_COUNTRY } from "src/constants/Countries";
import { replaceFullWidthNumerals } from "src/utils/replaceFullWidthNumerals";

import { DOBField } from "./DOBField";
import { COUNTRY_DATE_FORMATS, FORMAT_BY_REGION, MAXIMUM_AGE, MINIMUM_AGE } from "./constants";

type Props = {
  getDateInfo: (date: Moment, isValidDate: boolean, isLegalAge?: boolean) => void;
  initialValue?: string;
  isDOBInput?: boolean;
  disabled?: boolean;
};

export const OFDateInput = ({
  getDateInfo,
  initialValue,
  isDOBInput = false,
  disabled = false
}: Props) => {
  const {
    register,
    formState: { errors },
    control,
    watch,
    setFocus,
    setValue
  } = useForm({
    mode: "onBlur",
    reValidateMode: "onBlur",
    resolver: yupResolver(
      yup.object().shape({
        day: yup.string().required("required").default(""),
        month: yup.string().required("required").default(""),
        year: yup.string().required("required").default("")
      })
    )
  });

  useEffect(() => {
    if (initialValue) {
      const check = moment(initialValue, "YYYY/MM/DD");

      setValue("day", check.format("D"));
      setValue("month", check.format("M"));
      setValue("year", check.format("YYYY"));
    }
  }, [initialValue, setValue]);

  const day = watch("day")?.toString();
  const month = watch("month")?.toString();
  const year = watch("year")?.toString();

  const isFormValid = isEmpty(errors);
  const shouldValidateDate =
    isFormValid &&
    [1, 2].includes(day?.length) &&
    [1, 2].includes(month?.length) &&
    year?.length === 4;

  useEffect(() => {
    const date = moment(replaceFullWidthNumerals(`${day}.${month}.${year}`), "DD.MM.YYYY");

    if (shouldValidateDate) {
      // Only validate the date if it is not a DOBInput
      if (!isDOBInput) {
        return getDateInfo(date, date.isValid());
      }

      const age = moment().diff(date, "years");

      // Validate the age and return the result as the 3rd param
      if (age < MINIMUM_AGE || age > MAXIMUM_AGE) {
        getDateInfo(date, date.isValid(), false);
      } else {
        getDateInfo(date, date.isValid(), true);
      }
    } else {
      getDateInfo(date, false);
    }
  }, [shouldValidateDate, day, month, year, getDateInfo, isDOBInput]);

  const commonProps = {
    control,
    register,
    setFocus,
    disabled
  };

  return (
    <>
      <Flex gap="16px" w="max-content">
        {COUNTRY_DATE_FORMATS[CURRENT_COUNTRY].format.map(({ name, nextField }) => {
          return (
            <DOBField
              key={name}
              {...FORMAT_BY_REGION[name]}
              nextField={nextField}
              {...commonProps}
            />
          );
        })}
      </Flex>
    </>
  );
};
